// prop-types is library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

// Vision UI Dashboard React base styles
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import { useTranslation } from 'react-i18next';
import ShareButton from 'components/CustomButtons/ShareButton'; 

function GameInfoCard({ title, description, info, social, sellers, image, gameId }) {
  const labels = [];
  const values = [];
  const { size } = typography;
  const { t, i18n } = useTranslation();
  // Convert this form `objectKey` of the object key in to this `object key`
  Object.keys(info).forEach((el) => {
    if (typeof el === 'string') {
      if (el.match(/[A-Z\s]+/)) {
        const uppercaseLetter = Array.from(el).find((i) => i.match(/[A-Z]+/));
        if (uppercaseLetter) {
          const newElement = el.replace(uppercaseLetter, ` ${uppercaseLetter.toLowerCase()}`);
          labels.push(newElement);
        } else {
          labels.push(el);
        }
      } else {
        labels.push(el);
      }
    } else {
      labels.push(el);
    }
  });

  // Push the object values into the values array
  Object.values(info).forEach((el) => values.push(el));

  // Render the card info items
  const renderItems = labels.map((label, key) => (
    <VuiBox key={label} display="flex" py={1} pr={2}>
      <VuiTypography variant="button" color="text" fontWeight="regular" textTransform="capitalize">
        {label}: &nbsp;
      </VuiTypography>
      <VuiTypography variant="button" fontWeight="regular" color="white">
        &nbsp;{values[key]}
      </VuiTypography>
    </VuiBox>
  ));

  // Render the card social media icons
  const renderSocial = social.map(({ link, icon, color }) => (
    <VuiBox
      key={color}
      component="a"
      href={link}
      target="_blank"
      rel="noreferrer"
      fontSize={size.lg}
      color="white"
      pr={1}
      pl={0.5}
      lineHeight={1}
    >
      {icon}
    </VuiBox>
  ));

  // Render the card sellers media icons
  const renderSellers = sellers.map(({ link, icon, color }) => (
    <VuiBox
      key={color}
      component="a"
      href={link}
      target="_blank"
      rel="noreferrer"
      fontSize={size.lg}
      color="white"
      pr={1}
      pl={0.5}
      lineHeight={1}
    >
      {icon}
    </VuiBox>
  ));

  return (
    <Card
      sx={{
        height: "100%",
      }}
    >
      <VuiBox display="flex" mb="14px" justifyContent="space-between" alignItems="center">
        <VuiTypography variant="lg" fontWeight="bold" color="white" textTransform="capitalize">
          {title}
        </VuiTypography>
        {gameId && (
          <ShareButton 
            title={title}
            text={t('lookAtThisGame')} 
            url={`${process.env.REACT_APP_DEFAULT_DOMAIN}/game/${gameId}`}
          />
        )}
      </VuiBox>
      {image && (
        <VuiBox
          component="img"
          src={image}
          mb="8px"
          borderRadius="15px"
          sx={({ breakpoints }) => ({
            [breakpoints.up("xl")]: {
              width: "250px",
            },
          })}
        />
      )}
      <VuiBox>
        <VuiBox mb={2} lineHeight={1}>
          <VuiTypography variant="button" color="text" fontWeight="regular">
            {description}
          </VuiTypography>
        </VuiBox>
        <VuiBox opacity={0.3}>
          <Divider />
        </VuiBox>
        <VuiBox>
          {renderItems}
          {social.length > 0 && (
            <VuiBox display="flex" py={1} pr={2} color="white">
              <VuiTypography
                variant="button"
                fontWeight="regular"
                color="text"
                textTransform="capitalize"
              >
                social: &nbsp;
              </VuiTypography>
              {renderSocial}
            </VuiBox>
          )}
          {sellers.length > 0 && (
            <VuiBox display="flex" py={1} pr={2} color="white">
              <VuiTypography
                variant="button"
                fontWeight="regular"
                color="text"
                textTransform="capitalize"
              >
                {t('buyMe')}: &nbsp;
              </VuiTypography>
              {renderSellers}
            </VuiBox>
          )}
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

// Typechecking props for the GameInfoCard
GameInfoCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  info: PropTypes.objectOf(PropTypes.string).isRequired,
  social: PropTypes.arrayOf(PropTypes.object).isRequired,
  sellers: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default GameInfoCard;
